import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Linkedin, Mail, Phone, MapPin, ArrowUpRight, Download, Loader2 } from "lucide-react";
import { Logo } from "./Logo";
import { CONTACT } from "../data/content";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { submitNewsletter, submitProfileDownload } from "../lib/api";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Please enter your email"); return; }
    setLoading(true);
    try { const { data } = await submitNewsletter({ email }); toast.success(data.message); setEmail(""); }
    catch { toast.error("Something went wrong"); }
    finally { setLoading(false); }
  };

  const downloadProfile = async () => {
    // simple prompt for name/email via toast? Just open PDF and log lightweight lead.
    try {
      const guest = { name: "Guest", email: "guest@techmatesolution.net" };
      const { data } = await submitProfileDownload(guest);
      window.open(data.download_url, "_blank");
    } catch { toast.error("Could not start download"); }
  };

  return (
    <footer className="print-hide relative bg-[#020617] text-slate-300 mt-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <div className="[&_.text-foreground]:!text-white [&_.text-muted-foreground]:!text-slate-400"><Logo /></div>
            <p className="mt-5 text-sm leading-relaxed text-slate-400 max-w-sm">
              Strategic Technology Partner for Secure, Compliant &amp; Resilient Businesses. Engineered for Resilience.
            </p>
            <form onSubmit={subscribe} className="mt-6 flex gap-2 max-w-sm" data-testid="footer-newsletter-form">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                data-testid="footer-newsletter-email-input"
                className="bg-white/5 border-white/10 text-white placeholder:text-slate-500"
              />
              <Button type="submit" disabled={loading} data-testid="footer-newsletter-submit-btn" className="bg-secondary hover:bg-secondary/90 text-secondary-foreground rounded-md">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Subscribe"}
              </Button>
            </form>
            <button
              onClick={downloadProfile}
              data-testid="footer-download-profile-btn"
              className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/15 hover:border-white/30 px-4 py-2 text-sm font-semibold text-white transition-colors"
            >
              <Download className="h-4 w-4" /> Download Company Profile
            </button>
          </div>
          <div className="lg:col-span-2">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Solutions</div>
            <ul className="mt-4 space-y-2 text-sm">
              {[
                ["/services", "Cybersecurity Consulting"],
                ["/services", "Managed Security"],
                ["/services", "Cloud Security"],
                ["/services", "Identity Security"],
                ["/services", "Data Protection"],
              ].map(([to, l], i) => (
                <li key={i}><Link to={to} className="hover:text-white transition-colors" data-testid={`footer-solutions-link-${i}`}>{l}</Link></li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-2">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Compliance</div>
            <ul className="mt-4 space-y-2 text-sm">
              {["ISO 27001", "DPDP", "SOC 2", "RBI / SEBI", "CERT-In", "NIST CSF"].map((l, i) => (
                <li key={i}><Link to="/#compliance" className="hover:text-white transition-colors" data-testid={`footer-compliance-link-${i}`}>{l}</Link></li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-2">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Resources</div>
            <ul className="mt-4 space-y-2 text-sm">
              {[
                ["/resources", "Cybersecurity Blog"],
                ["/resources", "Case Studies"],
                ["/resources", "Whitepapers"],
                ["/resources", "Compliance Guides"],
                ["/resources", "Awareness"],
              ].map(([to, l], i) => (
                <li key={i}><Link to={to} className="hover:text-white transition-colors" data-testid={`footer-resources-link-${i}`}>{l}</Link></li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-2">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Contact</div>
            <ul className="mt-4 space-y-2 text-sm">
              <li className="flex items-start gap-2"><Phone className="h-4 w-4 mt-0.5" /> {CONTACT.phone}</li>
              <li className="flex items-start gap-2"><Mail className="h-4 w-4 mt-0.5" /> {CONTACT.email}</li>
              <li className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5" /> {CONTACT.address}</li>
              <li>
                <a href={CONTACT.linkedin} target="_blank" rel="noreferrer" data-testid="footer-linkedin-link" className="inline-flex items-center gap-2 hover:text-white">
                  <Linkedin className="h-4 w-4" /> LinkedIn <ArrowUpRight className="h-3 w-3" />
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <div>© {new Date().getFullYear()} Techmate Infosec Solutions. All rights reserved.</div>
          <div className="flex items-center gap-4">
            <span>Engineered for Resilience</span>
            <span className="hidden sm:inline">•</span>
            <Link to="/contact" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/contact" className="hover:text-white transition-colors">Terms</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
